<template>
    <form class="container row align-items-center bg-white px-0 py-0 mx-auto shadow-sm mt-2 search-form" @submit.prevent="handleSubmit">
        <div class="col-lg-2 col-sm-6 col-12 p-2 form-column xs-no-border">
            <div class="text-sm text-muted px-2">
                {{ $t('components.taxi_search_form.from') }}:
            </div>

            <div class="position-relative">
                <div class="pl-2 position-relative">
                    <vue-autosuggest
                        :suggestions="[{ data: startPlaces }]"
                        :input-props="{ id: 'StartPlaceInput', placeholder: $t('berbus.components.search_form.enter_address') }"
                        :get-suggestion-value="(s) => s.item.start_place"
                        :value="parameters.start_place ? parameters.start_place.start_place : ''"
                        :should-render-suggestions="(size, loading) => !loading && !isStartPlacesLoading && shouldShowStartPlacesNoResults"
                        component-attr-id-autosuggest="SearchPlace"
                        component-attr-class-autosuggest-results="autosuggest__results autosuggest__results_start_place"
                        class="vue-autosuggest"
                        @input="handleSearchStartPlaces"
                        @selected="handleStartPlaceSelected"
                    >
                        <template slot-scope="{ suggestion }">
                            <div :class="suggestion.item.start_is_airport ? 'is-airport' : 'is-city'">
                                {{ suggestion.item.start_place }}
                            </div>
                        </template>

                        <template v-if="startPlaces.length === 0" slot="after-suggestions">
                            <div class="no-results">
                                {{ $t('berbus.components.search_form.search_no_results') }}
                            </div>
                        </template>
                    </vue-autosuggest>

                    <div class="autosuggest-preloader" v-if="isStartPlacesLoading">
                        <div class="spinner-border spinner-border-sm" role="status">
                            <span class="sr-only">{{ $t('loading') }}...</span>
                        </div>
                    </div>
                </div>

                <button type="button" class="btn btn-sm btn-swap" @click="handleSwapLocations">
                    <font-awesome-icon icon="exchange-alt" />
                </button>
            </div>
        </div>

        <div class="col-lg-2 col-sm-6 col-12 p-2 form-column sm-no-border">
            <div class="text-sm text-muted px-2">
                {{ $t('components.taxi_search_form.to') }}:
            </div>

            <div class="pl-2 position-relative">
                <vue-autosuggest
                    :suggestions="[{ data: endPlaces }]"
                    :input-props="{ id: 'EndPlaceInput', placeholder: $t('berbus.components.search_form.enter_address') }"
                    :get-suggestion-value="(s) => s.item.end_place"
                    :value="parameters.end_place ? parameters.end_place.end_place : ''"
                    :should-render-suggestions="(size, loading) => !loading && !isEndPlacesLoading && shouldShowEndPlacesNoResults"
                    component-attr-id-autosuggest="EndPlace"
                    component-attr-class-autosuggest-results="autosuggest__results autosuggest__results_end_place"
                    class="vue-autosuggest"
                    @input="handleSearchEndPlaces"
                    @selected="handleEndPlaceSelected"
                >
                    <template slot-scope="{ suggestion }">
                        <div :class="suggestion.item.end_is_airport ? 'is-airport' : 'is-city'">
                            <span>{{ suggestion.item.end_place }}</span>
                        </div>
                    </template>

                    <template v-if="endPlaces.length === 0" slot="after-suggestions">
                        <div class="no-results">
                            {{ $t('berbus.components.search_form.search_no_results') }}
                        </div>
                    </template>
                </vue-autosuggest>

                <div class="autosuggest-preloader" v-if="isEndPlacesLoading">
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="sr-only">{{ $t('loading') }}...</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="col-lg-2 col-sm-6 col-12 p-2 form-column xs-no-border">
            <div class="row px-2">
                <div class="col-12">
                    <div class="text-sm text-muted">
                        {{ $t('departure_date') }}:
                    </div>
                </div>
            </div>
            <div class="row pr-3">
                <div class="col-12">
                    <date-picker
                        :placeholder="$t('components.taxi_search_form.date_pick')"
                        value-type="YYYY-MM-DD"
                        :disabled-date="notBeforeToday"
                        format="dd DD MMM"
                        :lang="datePickerLang"
                        ref="departureDatePicker"
                        v-model="parameters.departure_date"
                    />

                    <div class="plus-icon" @click="handleDepartureDateClick">
                        <font-awesome-icon icon="plus" />
                    </div>
                </div>
            </div>
        </div>

        <div class="col-lg-2 col-sm-6 col-12 p-2 form-column sm-no-border">
            <div class="row px-2">
                <div class="col-12">
                    <div class="text-sm text-muted">
                        {{ $t('departure_time') }}:
                    </div>
                </div>
            </div>
            <div class="row pr-3">
                <div class="col-12">
                    <vue-timepicker
                        :minute-interval="5"
                        :hour-range="departureTimeHourRange"
                        hide-clear-button
                        close-on-complete
                        :placeholder="$t('components.taxi_search_form.add_time')"
                        hide-disabled-hours
                        ref="departureTimePicker"
                        v-model="parameters.departure_time"
                        @open="handleDepartureTimeDropdownOpen"
                    />

                    <div class="plus-icon" @click="handleDepartureTimeClick">
                        <font-awesome-icon icon="plus" />
                    </div>
                </div>
            </div>
        </div>

        <div class="col-lg-2 col-sm-12 col-12 px-2 pb-2 form-column border-0">
            <div class="row px-2">
                <div class="col-12">
                    <div class="text-sm text-muted">
                        {{ $t('passengers') }}:
                    </div>
                </div>
            </div>
            <div class="row passengers-select-row">
                <div class="col-12">
                    <PassengersSelect :passengers.sync="parameters.passengers" />
                </div>
            </div>
        </div>

        <div class="col-lg-2 col-sm-12 col-12 px-0 h-100">
            <button type="submit" class="btn btn-primary w-100 h-100 py-lg-4 py-md-2">
                {{ $t('components.taxi_search_form.submit') }}
            </button>
        </div>
    </form>
</template>

<script>
    import DatePicker from 'vue2-datepicker';
    import VueTimepicker from 'vue2-timepicker';
    import 'vue2-datepicker/index.css';
    import { VueAutosuggest } from 'vue-autosuggest';

    import plLang from 'vue2-datepicker/locale/pl';
    import enLang from 'vue2-datepicker/locale/en';

    import { debounce as _debounce } from 'lodash-es';
    import toastr from 'toastr';

    import PassengersSelect from '@components/PassengersSelect';
    import { PASSENGER_TYPE_ADULT, PASSENGER_TYPE_CHILD_AGE_3 } from '@/constants';
    import TravelRepository from "@repositories/berbus/TravelRepository";
    import GoogleGeoAddress from "@/pages/BerbusPage/GoogleGeoAddress";
    import GoogleLatLngBoundsPreset from "@/pages/BerbusPage/GoogleLatLngBoundsPreset";

    const initDepartureDate = new Date();

    initDepartureDate.setHours(0, 0, 0, 0);
    initDepartureDate.setDate(initDepartureDate.getDate() + 1);

    export default {
        components: {
            DatePicker,
            VueTimepicker,
            VueAutosuggest,
            PassengersSelect,
        },

        props: {
            initialParameters: {
                type: Object,
                default: null,
            },

            startPlaces: {
                type: Array,
                required: true,
            },

            endPlaces: {
                type: Array,
                required: true,
            },

            isStartPlacesSearchable: {
                type: Boolean,
                default: true,
            },

            isEndPlacesSearchable: {
                type: Boolean,
                default: true,
            },

            isStartPlacesLoading: {
                type: Boolean,
                required: true,
            },

            isEndPlacesLoading: {
                type: Boolean,
                required: true,
            },
        },

        data() {
            return {
                parameters: {
                    start_place: null,
                    end_place: null,
                    departure_date: null,
                    departure_time: '',
                    passengers: [
                        {
                            type: PASSENGER_TYPE_ADULT,
                            quantity: 1,
                            min: 1,
                        },
                        {
                            type: PASSENGER_TYPE_CHILD_AGE_3,
                            quantity: 0,
                            min: 0,
                        }],
                },
                departureTimeHourRange: [],
                shouldShowStartPlacesNoResults: false,
                shouldShowEndPlacesNoResults: false,
            };
        },

        computed: {
            datePickerLang() {
                if (window.APP.locale === 'pl') {
                    return plLang;
                }

                return enLang;
            },

            passengersTotalQuantity() {
                return this.parameters.passengers
                    .map((passenger) => passenger.quantity)
                    .reduce((accumulator, quantity) => accumulator + quantity);
            },
        },

        created() {
            this.parameters.departure_date = this.formatDate(initDepartureDate);

            if (this.initialParameters) {
                this.parameters = Object.assign(this.parameters, this.initialParameters);
            }
        },

        methods: {
            handleSearchStartPlaces: _debounce(function (search) {
                return this.fetchStartPlaces(search);
            }, 700),

            handleSearchEndPlaces: _debounce(function (search) {
                return this.fetchEndPlaces(search);
            }, 700),

            handleStartPlaceSelected(place) {
                if (!place) {
                    return;
                }
                this.parameters.start_place = place.item;
                this.$emit('startPlacePicked', place.item);

                this.departureTimeSetup();
            },

            departureTimeSetup() {
                this.parameters.departure_time = '';
                this.$refs.departureTimePicker.minute = '';
                this.$refs.departureTimePicker.hour = '';
                this.departureTimeHourRange = [];
                if(this.parameters.start_place.hours){
                    this.departureTimeHourRange = [[this.getHourPartOfForDepartureTime(this.parameters.start_place.hours.from), this.getHourPartOfForDepartureTime(this.parameters.start_place.hours.to)]];
                }
                else{
                    this.googleFindAddressGeo(this.parameters.start_place.start_place).then((addresses) => {
                        const address = new GoogleGeoAddress(addresses);
                        //@FIXME translate to BO API
                        return TravelRepository.findZone(address.lat(), address.lng()).then(({ data }) => {
                            this.departureTimeHourRange = [[this.getHourPartOfForDepartureTime(data.data.hour_from), this.getHourPartOfForDepartureTime(data.data.hour_to)]];
                        });
                    });
                }
            },

            getHourPartOfForDepartureTime(time) {
                return time ? parseInt(time.replace(/:.*?$/, ''), 10) : '';
            },

            googleFindAddressGeo(address) {
                const geocoder = new window.google.maps.Geocoder();
                return new Promise((resolve, reject) => {
                    geocoder.geocode({
                        address,
                        bounds: GoogleLatLngBoundsPreset,
                        language: window.APP.locale,
                    }, (results, status) => {
                        if (status !== 'OK') {
                            this.criticalErrors.push(this.$t('berbus.pages.address.errors.geo_response_error'));
                            reject();
                        }
                        if (results.length === 0) {
                            this.criticalErrors.push(this.$t('berbus.pages.address.errors.incomplete_address'));
                            reject();
                        }
                        resolve(results);
                    });
                });
            },

            handleEndPlaceSelected(place) {
                if (!place) {
                    return;
                }

                this.parameters.end_place = place.item;

                this.$emit('endPlacePicked', place.item);
            },

            fetchStartPlaces(search) {
                this.shouldShowStartPlacesNoResults = search.length > 0;

                this.$emit('findStartPlaces', search);
            },

            fetchEndPlaces(search) {
                this.shouldShowEndPlacesNoResults = search.length > 0;

                this.$emit('findEndPlaces', search);
            },

            handleSubmit() {
                if (!this.parameters.start_place) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('from')}));

                    return;
                }

                if (!this.parameters.end_place) {
                    toastr.error(this.$t('field_is_required', {field: this.$t('to')}));

                    return;
                }

                if (!this.parameters.departure_date) {
                    toastr.error(this.$t('field_is_required', { field: this.$t('departure_date') }));

                    return;
                }

                if (!this.parameters.departure_time) {
                    toastr.error(this.$t('field_is_required', { field: this.$t('departure_time') }));

                    return;
                }

                if (!this.parameters.departure_time.match(/^[0-9]{2}:[0-9]{2}$/)) {
                    toastr.error(this.$t('time_invalid', { field: this.$t('departure_time') }));

                    return;
                }

                if (this.passengersTotalQuantity === 0) {
                    toastr.error(this.$t('validation.at_least_one_passenger_required'));

                    return;
                }

                const departureDate = new Date(`${this.parameters.departure_date} ${this.parameters.departure_time}`);
                const dayFromNowDate = new Date();
                dayFromNowDate.setDate(dayFromNowDate.getDate() + 2);

                if (departureDate < dayFromNowDate) {
                    toastr.error(this.$t('datetime_has_to_be_in_2_days_from_now', { field: this.$t('departure_time') }));

                    return;
                }

                this.$emit('search', JSON.parse(JSON.stringify(this.parameters)));
            },

            handleSwapLocations() {
                const newStartPlace = this.parameters.end_place ? {
                    start_place: this.parameters.end_place.end_place,
                    start_is_airport: this.parameters.end_place.end_is_airport,
                    vendor: this.parameters.end_place.vendor,
                    hours: this.parameters.end_place.hours,
                } : null;

                const newEndPlace = this.parameters.start_place ?  {
                    end_place: this.parameters.start_place.start_place,
                    end_is_airport: this.parameters.start_place.start_is_airport,
                    vendor: this.parameters.start_place.vendor,
                    hours: this.parameters.start_place.hours,
                } : null;

                this.parameters.start_place = newStartPlace;
                this.parameters.end_place = newEndPlace;

                this.$emit('swapPlaces');

                this.departureTimeSetup();
            },

            notBeforeToday(date) {
                return date < initDepartureDate;
            },

            handleDepartureDateClick() {
                this.$refs.departureDatePicker.openPopup();
            },

            handleDepartureTimeClick() {
                if(!this.parameters.start_place){
                    toastr.error(this.$t('field_is_required', {field: this.$t('from')}));
                    this.$refs.departureTimePicker.showDropdown = false;
                }
                else{
                    this.$refs.departureTimePicker.showDropdown = true;
                }
            },

            handleDepartureTimeDropdownOpen() {
                if(!this.parameters.start_place){
                    toastr.error(this.$t('field_is_required', {field: this.$t('from')}));
                    this.$refs.departureTimePicker.showDropdown = false;
                }
            },
        },
    };
</script>

<style scoped>
    .form-column:not(:last-child) {
        border-right: 1px solid lightgray;
    }

    .vue__time-picker >>> input.display-time {
        border: none;
        padding-top: 0px;
        width: 94%;
    }
    .vue__time-picker >>> input.display-time:focus {
        outline: none;
    }
    .vue__time-picker >>> input.display-time::placeholder {
        color:#5f6a70;
    }
    .mx-datepicker >>> .mx-input-wrapper .mx-input {
        border:none;
        padding-right: 20px;
        box-shadow: none;
        pointer-events: none;
    }
    .mx-datepicker >>> .mx-input-wrapper .mx-input::placeholder {
        font-size: 16px;
        color: #5f6a70;
    }
    .mx-datepicker >>> .mx-input-wrapper .mx-icon-calendar {
        display: none;
    }

    .plus-icon {
        position: absolute;
        top: 0.1rem;
        right: 0px;
        cursor: pointer;
        opacity: 0.7;
    }


    @media (min-width: 991px) {
        .passengers-select-row {
            height: 1.6rem;
        }
    }

    @media (max-width: 991px) {
        .sm-no-border {
            border: none !important;
        }
    }

    @media (max-width: 767px) {
        .xs-no-border {
            border: none !important;
        }
    }
</style>
